import * as React from "react";
import tw from "twin.macro";
import { useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";

import Button from "app/components/Button";
import SubHeader from "app/styles/SubHeader";

import { LoginPayload } from "app/api/busha/types";
import useLogin from "../hooks/useLogin";
import { loginDataType } from "..";
import Text from "app/styles/Text";
import { TextField } from "app/components/TextField";
import useSocialLogin from "../hooks/useSocialLogin";
import { useGetToken } from "../hooks/grecaptcha";
import { useAppState } from "app/providers/AppStateProvider";
import { PAYMENT_LINK } from "app/constants/variables";
import { getCheckoutDeepLink } from "app/lib/device";
import GoogleAuthFirebase from "app/components/GoogleAuthFirebase";

interface Props {
  onDone: (v: loginDataType) => void;
}
export default function LoginForm(props: Props) {
  const { onDone } = props;

  const { source, sourceId } = useAppState();

  const [showErrors, setShowErrors] = React.useState(false);

  const [authMethod, setAuthMethod] = useState<"password" | "google">("password");

  const [{ email, password, remember_me }, setState] = React.useState<
    Omit<LoginPayload, "token">
  >({
    email: "",
    password: "",
    // remember_me: true,
  });
  const formValid = !!password && !!email;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setState((s) => ({ ...s, [name]: value }));
  };

  const { getToken: getRecaptchaToken, loading: isRecaptchaLoading } = useGetToken();

  const { isLoading, login } = useLogin();

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!formValid) {
      setShowErrors(true);
      return;
    }
    setAuthMethod("password");

    const token = await getRecaptchaToken()

    login({ email, password, token, remember_me }, {
      onSuccess: (data) => {
        onDone({ ...data.data, email });
      }
    });
  };

  const { login: socialLogin, isLoading: isSocialLoginLoading } = useSocialLogin();

  const onGoogleAuthResponse = React.useCallback(
    async (idToken: string) => {
      setAuthMethod("google");
      const token = await getRecaptchaToken()

      socialLogin({ provider: "google", oauth_token: idToken, token }, {
        onSuccess: (data) => {
          onDone({ ...data.data.data, email });
        }
      });
    },
    [socialLogin, getRecaptchaToken, onDone, email]
  );

  const qrCodeValue = useMemo(() => {
    if (!sourceId || !source || source !== PAYMENT_LINK) return undefined;

    return getCheckoutDeepLink(sourceId);
  }, [source, sourceId]);

  return (
    <form tw="w-full flex flex-col gap-[32px]" onSubmit={onSubmit}>
      <div tw="flex flex-col gap-6">
        <div tw="flex flex-col gap-[8px]">
          <SubHeader css={tw`headline-medium`}>Login to Busha account</SubHeader>

          <Text tw="body-large text-text-mid">Complete your purchase by providing your payment details.</Text>
        </div>

        <div tw="flex flex-col gap-3">
          <TextField
            type="email"
            label="Email Address"
            name="email"
            value={email}
            onChange={handleChange}
            autoComplete="email"
            error={showErrors && !email.trim().length}
            hintText={
              showErrors && !email.trim().length ? "Email is required" : undefined
            }
          />

          <TextField
            type="password"
            label="Password"
            autoComplete="current-password"
            containerClassName="password"
            name="password"
            value={password}
            onChange={handleChange}
            error={showErrors && !password.trim().length}
            hintText={
              showErrors && !password.trim().length
                ? "Password is required"
                : undefined
            }
          />
        </div>
      </div>

      <div tw="flex flex-col gap-[16px]">
        <Button
          type="submit"
          fullWidth
          size="xBig"
          variant="primary"
          text="Login"
          disabled={!formValid}
          loading={isLoading || (isRecaptchaLoading && authMethod === "password")}
        />

        <GoogleAuthFirebase
          onAuthenticated={onGoogleAuthResponse}
          loading={isSocialLoginLoading || (isRecaptchaLoading && authMethod === "google")}
        />
      </div>

      {qrCodeValue &&
        (<div tw="flex-col gap-[16px] items-start border-t border-t-border-mid pt-[20px] hidden md:flex">
          <div>
            <h5 css={tw`headline-small text-navbar-active-label`}>Or scan QR code to pay in the Busha App</h5>
            <p css={tw`body-large text-text-mid`}>To complete this payment, you can open the Busha app or log in via the browser.</p>
          </div>

          <div tw="flex items-center justify-center p-[16px] bg-containment-tertiary rounded-[16px]">
          <QRCodeSVG tw="w-[120px] h-[120px]" value={qrCodeValue} />
          </div>
        </div>
        )}
    </form>
  );
}

